import { DEFAULT_ASSISTANT_AGENT_NAME, LoadedChat } from '@ardoq/api-types';
import { isTruthy } from '@ardoq/common-helpers';
import type { AgentInteractionResolved } from '@ardoq/api';
import type { AiThinkingStep } from '@ardoq/ai';
import {
  AssistantChatMessage,
  ChatMessage,
  ChatMessageContentEvent,
  ChatMessageContentPayload,
  ChatMessageDoneEvent,
  ChatMessageId,
  ChatMessageToolResultEvent,
  DecoratedSendMessageRequest,
  InfoContent,
  isAssistantChatMessage,
  isEmptyTextMessage,
  isFeedbackCardChatMessage,
  isInteractionMessage,
  isToolCallMessage,
  type UserFacingInteraction,
  type UserFacingInteractionContent,
  type UserFacingInteractionRequest,
} from './types';
import { createChatMessage } from './chatMessageFactory';
import { buildToolStepsByExchangeId, getToolStep } from './toolCatalog';
import { getLastMessage, getLastUserMessage } from './chatMessageSelectors';
import { ClientRequest, UserEvent } from 'sync/types';
import { currentTimestamp, parseToTimestamp } from '@ardoq/date-time';
import { trackAssistantEvent } from './trackAssistantEvent';
import { AssistantTrackingEvents } from './trackingEvents';
import type {
  AgentRunRestoration,
  SetFeedbackInputValuePayload,
} from './actions';
import { isCurrentChatMessageEvent } from './utils';
import { collectRenders, type RenderStore } from './renderMetadata';

export type ActiveExchange = {
  userMessageId: ChatMessageId | null;
  assistantMessageId: ChatMessageId;
};

export type AssistantMessagesState = {
  messages: ChatMessage[];
  renderMetadata: RenderStore;
  // Denormalized from assistantState$ — reducers in a reducedStream cannot
  // access other streams, so these are duplicated here for WebSocket event
  // validation and message content parsing.
  _currentActiveRequest: string | null;
  _activeExchange: ActiveExchange | null;
  _hasPythonAgentsFeature: boolean;
};

export const defaultAssistantMessagesState: AssistantMessagesState = {
  messages: [],
  renderMetadata: {},
  _currentActiveRequest: null,
  _activeExchange: null,
  _hasPythonAgentsFeature: false,
};

/**
 * Parses message content to extract answer and reasoning.
 * API sends JSON as string. This parses the string into an object.
 */
export const parseMessageContent = (
  message: string
): {
  content?: string;
  reasoning?: string;
  tour?: ChatMessageContentPayload['tour'];
  isAnswerFinal?: boolean;
} => {
  try {
    const parsed = JSON.parse(message) as ChatMessageContentPayload;
    const keys = Object.entries(parsed)
      .filter(([, value]) => value !== null && value !== undefined)
      .map(([key]) => key);
    const answerIndex = keys.indexOf('answer');
    return {
      content: parsed.answer ?? '',
      reasoning: parsed.reasoning,
      tour: parsed.tour,
      isAnswerFinal: answerIndex !== -1 && answerIndex < keys.length - 1,
    };
  } catch {
    return {};
  }
};

// --- Messages-only reducers ---

const updateMessage = (
  state: AssistantMessagesState,
  messageId: ChatMessageId,
  update: (message: ChatMessage) => ChatMessage
): AssistantMessagesState => ({
  ...state,
  messages: state.messages.map(message =>
    message.messageId === messageId ? update(message) : message
  ),
});

const getMessageById = (
  messages: ChatMessage[],
  messageId: ChatMessageId
): ChatMessage | undefined =>
  messages.find(message => message.messageId === messageId);

const updateStreamingMessage = (
  state: AssistantMessagesState,
  update: (message: AssistantChatMessage) => ChatMessage
): AssistantMessagesState => {
  const messageId = state._activeExchange?.assistantMessageId;
  if (!messageId) return state;

  const message = getMessageById(state.messages, messageId);
  if (!message || !isAssistantChatMessage(message)) return state;

  return updateMessage(state, messageId, () => update(message));
};

const toPendingContent = (
  interactionId: string,
  request: UserFacingInteractionRequest
): UserFacingInteractionContent => {
  switch (request.kind) {
    case 'user_dropdown':
      return {
        kind: 'user_dropdown',
        interactionId,
        status: 'pending',
        request,
      };
    case 'user_input':
      return { kind: 'user_input', interactionId, status: 'pending', request };
    case 'user_inline_options':
      return {
        kind: 'user_inline_options',
        interactionId,
        status: 'pending',
        request,
      };
  }
};

const handleUserInputRequested = (
  state: AssistantMessagesState,
  { interactionId, exchangeId, request, timestamp }: UserFacingInteraction
): AssistantMessagesState => {
  const isAlreadyAdded = state.messages.some(
    message =>
      isInteractionMessage(message) &&
      message.content.interactionId === interactionId
  );
  if (isAlreadyAdded) return state;

  const interaction = createChatMessage({
    role: 'interaction',
    content: toPendingContent(interactionId, request),
    exchangeId,
    timestamp: parseToTimestamp(timestamp),
  });

  const assistantIndex = state.messages.findIndex(
    message => message.messageId === state._activeExchange?.assistantMessageId
  );

  const insertionIndex =
    assistantIndex === -1 ? state.messages.length : assistantIndex;

  return {
    ...state,
    messages: [
      ...state.messages.slice(0, insertionIndex),
      interaction,
      ...state.messages.slice(insertionIndex),
    ],
  };
};

const handleInteractionResolved = (
  state: AssistantMessagesState,
  { interactionId, result }: AgentInteractionResolved
): AssistantMessagesState => {
  if (result.kind === 'ui_command') return state;

  return {
    ...state,
    messages: state.messages.map(message => {
      if (
        !isInteractionMessage(message) ||
        message.content.interactionId !== interactionId
      )
        return message;

      const { content } = message;
      switch (content.kind) {
        case 'user_dropdown':
          if (result.kind !== 'user_dropdown') return message;
          return {
            ...message,
            content: { ...content, status: 'resolved', result },
          };
        case 'user_input':
          if (result.kind !== 'user_input') return message;
          return {
            ...message,
            content: { ...content, status: 'resolved', result },
          };
        case 'user_inline_options':
          if (result.kind !== 'user_inline_options') return message;
          return {
            ...message,
            content: { ...content, status: 'resolved', result },
          };
      }
    }),
  };
};

const markExchangeInteractionsAsCancelled = (
  messages: ChatMessage[],
  exchangeId: string | null
): ChatMessage[] =>
  messages.map(message =>
    isInteractionMessage(message) &&
    message.content.status === 'pending' &&
    message.exchangeId === exchangeId
      ? { ...message, content: { ...message.content, status: 'cancelled' } }
      : message
  );

const handleSetFeedbackInputValue = (
  state: AssistantMessagesState,
  { messageId, value }: SetFeedbackInputValuePayload
): AssistantMessagesState =>
  updateMessage(state, messageId, message => ({
    ...message,
    additionalFeedbackInputValue: value,
  }));

const handleShowAdditionalFeedbackInput = (
  state: AssistantMessagesState,
  messageId: ChatMessageId
): AssistantMessagesState =>
  updateMessage(state, messageId, message => ({
    ...message,
    additionalFeedbackInputIsVisible: true,
  }));

const handleShowFeedbackForm = (
  state: AssistantMessagesState,
  messageId: ChatMessageId
): AssistantMessagesState =>
  updateMessage(state, messageId, message => ({
    ...message,
    feedbackFormIsVisible: true,
    feedbackSuccessMessageIsVisible: false,
    additionalFeedbackInputIsVisible: false,
  }));

const handleHideFeedbackForm = (
  state: AssistantMessagesState,
  messageId: ChatMessageId
): AssistantMessagesState =>
  updateMessage(state, messageId, message => ({
    ...message,
    feedbackFormIsVisible: false,
  }));

const handleShowFeedbackFormSuccessMessage = (
  state: AssistantMessagesState,
  messageId: ChatMessageId
): AssistantMessagesState =>
  updateMessage(state, messageId, message => ({
    ...message,
    feedbackSuccessMessageIsVisible: true,
  }));

const handleToggleReasoningVisible = (
  state: AssistantMessagesState,
  messageId: ChatMessageId
): AssistantMessagesState =>
  updateMessage(state, messageId, message => ({
    ...message,
    isReasoningVisible: !message.isReasoningVisible,
  }));

const appendThinkingStep = (
  state: AssistantMessagesState,
  { clientRequestId, step }: { clientRequestId: string; step: AiThinkingStep }
): AssistantMessagesState => {
  if (state._currentActiveRequest !== clientRequestId) return state;

  return updateStreamingMessage(state, message => ({
    ...message,
    thinkingSteps: [...(message.thinkingSteps ?? []), step],
  }));
};

// --- Cross-cutting reducers (messages slice) ---

const handleSendMessage = (
  state: AssistantMessagesState,
  {
    content,
    timestamp,
    clientRequestId,
    agentType,
    documents,
  }: DecoratedSendMessageRequest
): AssistantMessagesState => {
  const documentMessages: ChatMessage[] = (documents ?? []).map(document =>
    createChatMessage({ content: document, role: 'document' })
  );
  const userMessage = content.trim()
    ? createChatMessage({ content, role: 'user', timestamp })
    : null;
  const assistantMessage = createChatMessage({
    content: '',
    role: 'assistant',
    agentType,
  });

  return {
    ...state,
    messages: [
      ...state.messages,
      ...documentMessages,
      ...(userMessage ? [userMessage] : []),
      assistantMessage,
    ],
    _currentActiveRequest: clientRequestId,
    _activeExchange: {
      userMessageId: userMessage?.messageId ?? null,
      assistantMessageId: assistantMessage.messageId,
    },
  };
};

const toCapturedAgentName = (
  message: ChatMessage,
  reportedAgentName: string | null | undefined
): string | undefined => {
  const agentName = reportedAgentName?.trim();
  if (!agentName || agentName.toLowerCase() === DEFAULT_ASSISTANT_AGENT_NAME) {
    return message.agentName;
  }
  return agentName;
};

const handleMessageContent = (
  state: AssistantMessagesState,
  event: UserEvent<ClientRequest<ChatMessageContentEvent>>
): AssistantMessagesState => {
  if (!isCurrentChatMessageEvent(state._currentActiveRequest, event)) {
    // eslint-disable-next-line ardoq-lint/follow-operations-pattern
    trackAssistantEvent(
      AssistantTrackingEvents.UNKOWN_MESSAGE_CONTENT_RECEIVED_AI,
      {
        clientRequestId: event.data.clientRequestId,
      }
    );
    return state;
  }

  const parsedContent = parseMessageContent(event.data.message);

  return updateStreamingMessage(state, message => ({
    ...message,
    ...parsedContent,
    agentName: toCapturedAgentName(message, event.data.agent_name),
    // eslint-disable-next-line ardoq-lint/follow-operations-pattern
    timestamp: currentTimestamp(),
  }));
};

const handleToolResult = (
  state: AssistantMessagesState,
  event: UserEvent<ClientRequest<ChatMessageToolResultEvent>>
): AssistantMessagesState => {
  if (
    !isCurrentChatMessageEvent(state._currentActiveRequest, event) ||
    event.data.metadata === undefined
  ) {
    return state;
  }

  return {
    ...state,
    renderMetadata: {
      ...state.renderMetadata,
      ...collectRenders([event.data.metadata]),
    },
  };
};

const setIsCurrentChatMessageDone = (
  state: AssistantMessagesState,
  event: UserEvent<ClientRequest<ChatMessageDoneEvent>>
): AssistantMessagesState => {
  const currentActiveRequest = state._currentActiveRequest;
  if (!currentActiveRequest) return state;

  if (!isCurrentChatMessageEvent(currentActiveRequest, event)) return state;

  if (event.data.cancelled) {
    const messages = markExchangeInteractionsAsCancelled(
      state.messages,
      currentActiveRequest
    );
    const activeExchange = state._activeExchange;

    if (!activeExchange) {
      return { ...state, messages, _currentActiveRequest: null };
    }

    const { userMessageId, assistantMessageId } = activeExchange;

    return {
      ...state,
      messages: messages
        .filter(message => message.messageId !== assistantMessageId)
        .map(message =>
          message.messageId === userMessageId
            ? { ...message, isCancelled: true }
            : message
        ),
      _currentActiveRequest: null,
      _activeExchange: null,
    };
  }

  return {
    ...state,
    _currentActiveRequest: null,
    _activeExchange: null,
  };
};

const setSendMessageError = (
  state: AssistantMessagesState
): AssistantMessagesState => ({
  ...state,
  messages: markExchangeInteractionsAsCancelled(
    state.messages,
    state._currentActiveRequest
  ).filter(message => !isEmptyTextMessage(message)),
  _currentActiveRequest: null,
  _activeExchange: null,
});

const handleAppendUserFriendlyChatError = (
  state: AssistantMessagesState,
  { content, errorCode }: { content: string; errorCode: string }
): AssistantMessagesState => ({
  ...state,
  messages: [
    ...markExchangeInteractionsAsCancelled(
      state.messages,
      state._currentActiveRequest
    ).filter(message => !isEmptyTextMessage(message)),
    createChatMessage({ role: 'assistant', content, errorCode }),
  ],
  _currentActiveRequest: null,
  _activeExchange: null,
});

const handleAppendInfoMessage = (
  state: AssistantMessagesState,
  content: InfoContent
): AssistantMessagesState => ({
  ...state,
  messages: [...state.messages, createChatMessage({ role: 'info', content })],
});

const handleDismissFeedbackCard = (
  state: AssistantMessagesState,
  messageId: ChatMessageId
): AssistantMessagesState => ({
  ...state,
  messages: state.messages.filter(
    message =>
      !(isFeedbackCardChatMessage(message) && message.messageId === messageId)
  ),
});

const handleSendMessageFailed = (
  state: AssistantMessagesState
): AssistantMessagesState => ({
  ...state,
  messages: markExchangeInteractionsAsCancelled(
    state.messages,
    state._currentActiveRequest
  ).filter(message => !isEmptyTextMessage(message)),
  _currentActiveRequest: null,
  _activeExchange: null,
});

const handleChatTimedOut = (
  state: AssistantMessagesState
): AssistantMessagesState => ({
  ...state,
  messages: markExchangeInteractionsAsCancelled(
    state.messages,
    state._currentActiveRequest
  ),
  _currentActiveRequest: null,
  _activeExchange: null,
});

const resetMessages = (
  state: AssistantMessagesState
): AssistantMessagesState => ({
  ...defaultAssistantMessagesState,
  _hasPythonAgentsFeature: state._hasPythonAgentsFeature,
});

const handleHistoricChatLoaded = (
  state: AssistantMessagesState,
  payload: LoadedChat
): AssistantMessagesState => {
  const toolStepsByExchangeId = buildToolStepsByExchangeId(payload.messages);

  const messages: ChatMessage[] = payload.messages
    .filter(message => !isToolCallMessage(message))
    .map(message => {
      if (message.role === 'document') {
        return createChatMessage({
          ...message,
          timestamp: parseToTimestamp(message.timestamp),
        });
      }

      if (message.role === 'interaction') {
        const { content } = message;

        if (content.kind === 'ui_command') return null;

        return createChatMessage({
          ...message,
          content,
          timestamp: parseToTimestamp(message.timestamp),
        });
      }

      const parsedContent = parseMessageContent(message.content);
      const thinkingSteps =
        message.role === 'assistant'
          ? toolStepsByExchangeId[message.exchangeId]
          : undefined;

      const msg: ChatMessage = createChatMessage({
        ...message,
        ...parsedContent,
        timestamp: parseToTimestamp(message.timestamp),
      });
      if (thinkingSteps) msg.thinkingSteps = thinkingSteps;
      return msg;
    })
    .filter(isTruthy);

  return {
    ...state,
    messages,
    renderMetadata: collectRenders(Object.values(payload.renderMetadata)),
    _currentActiveRequest: null,
    _activeExchange: null,
  };
};

const restoreAgentRunTarget = (
  state: AssistantMessagesState,
  { exchangeId }: { exchangeId: string }
): AssistantMessagesState => {
  const lastMessage = getLastMessage(state.messages);
  const existingPlaceholder =
    lastMessage !== undefined &&
    isAssistantChatMessage(lastMessage) &&
    isEmptyTextMessage(lastMessage)
      ? lastMessage
      : undefined;
  const placeholder =
    existingPlaceholder ??
    createChatMessage({ role: 'assistant', content: '' });

  return {
    ...state,
    messages: existingPlaceholder
      ? state.messages
      : [...state.messages, placeholder],
    _currentActiveRequest: exchangeId,
    _activeExchange: {
      userMessageId: getLastUserMessage(state.messages)?.messageId ?? null,
      assistantMessageId: placeholder.messageId,
    },
  };
};

const handleAgentRunWatchRestoreFailed = (
  state: AssistantMessagesState,
  { exchangeId }: { exchangeId: string }
): AssistantMessagesState => {
  if (state._currentActiveRequest !== exchangeId) return state;

  const placeholderId = state._activeExchange?.assistantMessageId;
  const placeholder = placeholderId
    ? getMessageById(state.messages, placeholderId)
    : undefined;

  return {
    ...state,
    messages:
      placeholder && isEmptyTextMessage(placeholder)
        ? state.messages.filter(message => message.messageId !== placeholderId)
        : state.messages,
    _currentActiveRequest: null,
    _activeExchange: null,
  };
};

const restoreAgentRunProgress = (
  state: AssistantMessagesState,
  {
    exchangeId,
    message: contentMessage,
    toolCalls,
    planningStep,
    renderMetadata,
  }: AgentRunRestoration
): AssistantMessagesState => {
  if (state._currentActiveRequest !== exchangeId) return state;

  const retainedThinkingSteps = toolCalls
    .filter(toolCall => Boolean(toolCall.action_description))
    .map(toolCall =>
      getToolStep(toolCall.action_description!, toolCall.action_category)
    );
  const withRenders = {
    ...state,
    renderMetadata: { ...state.renderMetadata, ...renderMetadata },
  };
  const withProgress = updateStreamingMessage(withRenders, message => ({
    ...message,
    thinkingSteps:
      retainedThinkingSteps.length > 0
        ? retainedThinkingSteps
        : message.thinkingSteps?.length
          ? message.thinkingSteps
          : [planningStep],
  }));

  if (!contentMessage) return withProgress;

  return updateStreamingMessage(withProgress, message => ({
    ...message,
    ...parseMessageContent(contentMessage),
  }));
};

const handleAgentRunRestored = (
  state: AssistantMessagesState,
  restoration: AgentRunRestoration
): AssistantMessagesState =>
  restoreAgentRunProgress(
    restoreAgentRunTarget(state, restoration),
    restoration
  );

export const assistantMessagesOperations = {
  handleUserInputRequested,
  handleInteractionResolved,
  handleSetFeedbackInputValue,
  handleShowAdditionalFeedbackInput,
  handleShowFeedbackForm,
  handleHideFeedbackForm,
  handleShowFeedbackFormSuccessMessage,
  handleToggleReasoningVisible,
  appendThinkingStep,
  handleSendMessage,
  handleMessageContent,
  handleToolResult,
  setIsCurrentChatMessageDone,
  setSendMessageError,
  handleAppendUserFriendlyChatError,
  handleAppendInfoMessage,
  handleDismissFeedbackCard,
  handleSendMessageFailed,
  handleChatTimedOut,
  resetMessages,
  handleHistoricChatLoaded,
  handleAgentRunRestored,
  handleAgentRunWatchRestoreFailed,
};
